<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import KanbanBoard from 'dashboard/components-next/kanban/KanbanBoard.vue';
import BoardLabelSelector from 'dashboard/components-next/kanban/BoardLabelSelector.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const boards = computed(() => getters['kanban/getBoards'].value);
const uiFlags = computed(() => getters['kanban/getUIFlags'].value);

const selectedBoardId = ref(null);
const boardOptions = computed(() =>
  boards.value.map(board => ({ value: String(board.id), label: board.name }))
);
const selectMenuValue = computed(() =>
  selectedBoardId.value ? String(selectedBoardId.value) : ''
);
const selectedBoard = computed(() =>
  getters['kanban/getBoardById'].value(selectedBoardId.value)
);
const columns = computed(() =>
  getters['kanban/getColumnsByBoardId'].value(selectedBoardId.value)
);
const cards = computed(() =>
  getters['kanban/getCardsByBoardId'].value(selectedBoardId.value)
);
const isLoadingBoard = computed(
  () => uiFlags.value.isFetchingColumns || uiFlags.value.isFetchingCards
);

const boardDialogRef = ref(null);
const editingBoard = ref(null);
const boardName = ref('');
const boardDescription = ref('');
const isSavingBoard = ref(false);
const showBoardDeleteDialog = ref(false);

const basisLabelIds = ref([]);
const entryRequiredLabelIds = ref([]);

const allLabels = computed(() => store.getters['labels/getLabels'] || []);

const resetBoardDialog = () => {
  basisLabelIds.value = [];
  entryRequiredLabelIds.value = [];
};

const selectBoard = value => {
  selectedBoardId.value = Number(value);
};

const fetchBoardData = async boardId => {
  try {
    await Promise.all([
      store.dispatch('kanban/getColumns', boardId),
      store.dispatch('kanban/getCards', boardId),
    ]);
  } catch (error) {
    useAlert(t('KANBAN.LOAD_ERROR_MESSAGE'));
  }
};

watch(selectedBoardId, boardId => {
  if (boardId) {
    fetchBoardData(boardId);
  }
});

onMounted(async () => {
  try {
    await store.dispatch('kanban/getBoards');
    if (boards.value.length) {
      selectedBoardId.value = boards.value[0].id;
    }
    if (!allLabels.value.length) {
      store.dispatch('labels/get');
    }
  } catch (error) {
    useAlert(t('KANBAN.LOAD_ERROR_MESSAGE'));
  }
});

const openCreateBoardDialog = () => {
  editingBoard.value = null;
  boardName.value = '';
  boardDescription.value = '';
  resetBoardDialog();
  boardDialogRef.value?.open();
};

const openEditBoardDialog = () => {
  editingBoard.value = selectedBoard.value;
  boardName.value = selectedBoard.value?.name || '';
  boardDescription.value = selectedBoard.value?.description || '';
  basisLabelIds.value =
    selectedBoard.value?.settings?.percentage_basis_label_ids?.map(Number) ||
    [];
  entryRequiredLabelIds.value =
    selectedBoard.value?.settings?.entry_required_label_ids?.map(Number) || [];
  boardDialogRef.value?.open();
};

const closeBoardDialog = () => {
  boardDialogRef.value?.close();
};

const saveBoard = async () => {
  isSavingBoard.value = true;
  try {
    const payload = {
      name: boardName.value.trim(),
      description: boardDescription.value,
      percentage_basis_label_ids: basisLabelIds.value,
      entry_required_label_ids: entryRequiredLabelIds.value,
    };
    if (editingBoard.value) {
      await store.dispatch('kanban/updateBoard', {
        id: editingBoard.value.id,
        ...payload,
      });
      useAlert(t('KANBAN.BOARDS.API.SUCCESS_UPDATE'));
    } else {
      const board = await store.dispatch('kanban/createBoard', payload);
      useAlert(t('KANBAN.BOARDS.API.SUCCESS_CREATE'));
      selectedBoardId.value = board.id;
    }
    closeBoardDialog();
  } catch (error) {
    useAlert(t('KANBAN.BOARDS.API.ERROR_MESSAGE'));
  } finally {
    isSavingBoard.value = false;
  }
};

const openBoardDeleteDialog = () => {
  showBoardDeleteDialog.value = true;
};

const closeBoardDeleteDialog = () => {
  showBoardDeleteDialog.value = false;
};

const confirmBoardDeletion = async () => {
  try {
    await store.dispatch('kanban/deleteBoard', selectedBoardId.value);
    useAlert(t('KANBAN.BOARDS.API.SUCCESS_DELETE'));
    closeBoardDeleteDialog();
    selectedBoardId.value = boards.value[0]?.id || null;
  } catch (error) {
    useAlert(t('KANBAN.BOARDS.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full p-6 bg-n-surface-1">
    <header class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-col gap-1">
        <h1 class="text-lg font-medium text-n-slate-12">
          {{ t('KANBAN.BOARDS.PAGE_TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-10">
          {{ t('KANBAN.BOARDS.PAGE_DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <SelectMenu
          v-if="boardOptions.length"
          :options="boardOptions"
          :model-value="selectMenuValue"
          :label="
            selectedBoard ? selectedBoard.name : t('KANBAN.BOARDS.SELECT_BOARD')
          "
          sub-menu-position="bottom"
          @update:model-value="selectBoard"
        />
        <template v-if="isAdmin && selectedBoardId">
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            class="!px-2"
            icon="i-lucide-pencil-line"
            :aria-label="t('KANBAN.BOARDS.EDIT_TITLE')"
            :title="t('KANBAN.BOARDS.EDIT_TITLE')"
            @click="openEditBoardDialog"
          />
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            class="!px-2"
            icon="i-lucide-trash"
            :aria-label="t('KANBAN.BOARDS.DELETE_TITLE')"
            :title="t('KANBAN.BOARDS.DELETE_TITLE')"
            @click="openBoardDeleteDialog"
          />
        </template>
        <Button
          v-if="isAdmin"
          size="sm"
          :label="t('KANBAN.BOARDS.NEW_BOARD')"
          icon="i-lucide-plus"
          @click="openCreateBoardDialog"
        />
      </div>
    </header>

    <div
      v-if="!boards.length && !uiFlags.isFetchingBoards"
      class="flex flex-col items-center justify-center flex-1 gap-3"
    >
      <span
        class="flex items-center justify-center w-12 h-12 rounded-full bg-n-alpha-2"
      >
        <Icon icon="i-lucide-kanban-square" class="size-6 text-n-slate-10" />
      </span>
      <div class="flex flex-col items-center gap-1">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.BOARDS.EMPTY_TITLE') }}
        </h2>
        <p class="max-w-sm text-sm text-center text-n-slate-10">
          {{ t('KANBAN.BOARDS.EMPTY_DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="isAdmin"
        :label="t('KANBAN.BOARDS.NEW_BOARD')"
        icon="i-lucide-plus"
        @click="openCreateBoardDialog"
      />
    </div>

    <div
      v-else-if="!selectedBoardId && uiFlags.isFetchingBoards"
      class="flex items-center justify-center flex-1"
    >
      <Spinner class="text-n-slate-10" />
    </div>

    <KanbanBoard
      v-else-if="selectedBoardId && selectedBoard"
      :board-id="selectedBoardId"
      :board="selectedBoard"
      :columns="columns"
      :cards="cards"
      :is-loading="isLoadingBoard"
    />

    <Dialog
      ref="boardDialogRef"
      :title="
        editingBoard
          ? t('KANBAN.BOARDS.EDIT_TITLE')
          : t('KANBAN.BOARDS.CREATE_TITLE')
      "
      :show-cancel-button="false"
      :show-confirm-button="false"
      @close="closeBoardDialog"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="boardName"
          :label="t('KANBAN.BOARDS.NAME_LABEL')"
          :placeholder="t('KANBAN.BOARDS.NAME_PLACEHOLDER')"
          autofocus
        />
        <TextArea
          v-model="boardDescription"
          :label="t('KANBAN.BOARDS.DESCRIPTION_LABEL')"
          :placeholder="t('KANBAN.BOARDS.DESCRIPTION_PLACEHOLDER')"
          :max-length="500"
          resize
        />
        <BoardLabelSelector
          v-model:selected-ids="basisLabelIds"
          :title="t('KANBAN.BOARDS.PERCENTAGE_BASIS_LABEL')"
          :placeholder="t('KANBAN.BOARDS.PERCENTAGE_BASIS_PLACEHOLDER')"
          :empty-state="t('KANBAN.BOARDS.NO_LABELS')"
          :add-label="t('KANBAN.BOARDS.ADD_LABEL')"
          :labels="allLabels"
        />
        <BoardLabelSelector
          v-model:selected-ids="entryRequiredLabelIds"
          :title="t('KANBAN.BOARDS.ENTRY_REQUIRED_LABEL')"
          :placeholder="t('KANBAN.BOARDS.ENTRY_REQUIRED_PLACEHOLDER')"
          :empty-state="t('KANBAN.BOARDS.NO_LABELS')"
          :add-label="t('KANBAN.BOARDS.ADD_LABEL')"
          :labels="allLabels"
        />
        <p class="text-xs text-n-slate-10">
          {{ t('KANBAN.BOARDS.ENTRY_REQUIRED_HINT') }}
        </p>
      </div>
      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button
            variant="faded"
            class="w-full"
            :label="t('KANBAN.BOARDS.CANCEL')"
            @click="closeBoardDialog"
          />
          <Button
            color="blue"
            class="w-full"
            :label="t('KANBAN.BOARDS.SAVE')"
            :is-loading="isSavingBoard"
            :disabled="isSavingBoard || !boardName.trim()"
            type="submit"
            @click="saveBoard"
          />
        </div>
      </template>
    </Dialog>

    <woot-delete-modal
      v-model:show="showBoardDeleteDialog"
      :on-close="closeBoardDeleteDialog"
      :on-confirm="confirmBoardDeletion"
      :title="t('KANBAN.BOARDS.DELETE_TITLE')"
      :message="t('KANBAN.BOARDS.DELETE_CONFIRM_MESSAGE')"
      :confirm-text="t('KANBAN.BOARDS.CONFIRM_DELETE')"
      :reject-text="t('KANBAN.BOARDS.REJECT_DELETE')"
    />
  </section>
</template>
